import type { TFunction } from "i18next";
import { z } from "zod";
import { type CompanyProfileResponse, type CompanyProfileUpdate, schemas } from "@/api";
import { formatNumber } from "@/i18n/format";

// Field rules (max lengths, founded-year range) come from the generated upsert schema.
const update = schemas.zCompanyProfileUpdate.shape;
const text = (field: (typeof update)["company_name"]) => field.unwrap().unwrap();
const foundedYear = update.founded_year.unwrap().unwrap();

export const TERMINOLOGY_LIMIT = text(update.key_terminology).maxLength ?? 5000;
export const GUIDANCE_LIMIT = text(update.brand_style).maxLength ?? 2000;
export const LOGO_TYPES = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"];
export const LOGO_MAX_BYTES = 5 * 1024 * 1024;

const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
export const isHex = (value: string) => HEX.test(value);

export const splitAccents = (value: string) =>
  value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

export function companySchema(t: TFunction<"adminCompany">) {
  const hex = z.string().refine((v) => v === "" || isHex(v.trim()), { error: t("errors.hex") });
  return z.object({
    companyName: text(update.company_name)
      .trim()
      .min(1, { error: t("errors.nameRequired") }),
    industry: text(update.industry),
    website: text(update.website),
    description: text(update.description),
    mission: text(update.mission),
    values: text(update.values),
    foundedYear: z
      .string()
      .refine((v) => v.trim() === "" || foundedYear.safeParse(Number(v)).success, { error: t("errors.foundedYear") }),
    headquarters: text(update.headquarters),
    employeeCount: text(update.employee_count),
    productsServices: text(update.products_services),
    // `id` is form-only (a stable React key for the row); formToBody drops it.
    leadership: z.array(schemas.zLeadershipEntry.extend({ id: z.string() })),
    keyTerminology: z.string().max(TERMINOLOGY_LIMIT, {
      error: t("errors.tooLong", { max: formatNumber(TERMINOLOGY_LIMIT) }),
    }),
    brandStyle: z.string().max(GUIDANCE_LIMIT, {
      error: t("errors.tooLong", { max: formatNumber(GUIDANCE_LIMIT) }),
    }),
    primary: hex,
    secondary: hex,
    background: hex,
    textColor: hex,
    accents: z.string().refine((v) => splitAccents(v).every(isHex), { error: t("errors.accents") }),
    headingFont: z.string(),
    bodyFont: z.string(),
    tone: z.string(),
    knowledgeBase: z.string(),
  });
}

export type CompanyFormValues = z.infer<ReturnType<typeof companySchema>>;

const str = (value: unknown) => (typeof value === "string" ? value : "");
const record = (value: unknown) => (value && typeof value === "object" ? (value as Record<string, unknown>) : {});

export function profileToForm(profile: CompanyProfileResponse | null | undefined): CompanyFormValues {
  const kit = record(profile?.brand_kit);
  const palette = record(kit.palette);
  const fonts = record(kit.fonts);
  return {
    companyName: profile?.company_name ?? "",
    industry: profile?.industry ?? "",
    website: profile?.website ?? "",
    description: profile?.description ?? "",
    mission: profile?.mission ?? "",
    values: profile?.values ?? "",
    foundedYear: profile?.founded_year?.toString() ?? "",
    headquarters: profile?.headquarters ?? "",
    employeeCount: profile?.employee_count ?? "",
    productsServices: profile?.products_services ?? "",
    leadership: (profile?.leadership ?? []).map(({ name, title }) => ({ id: newRowId(), name, title })),
    keyTerminology: profile?.key_terminology ?? "",
    brandStyle: profile?.brand_style ?? "",
    primary: str(palette.primary),
    secondary: str(palette.secondary),
    background: str(palette.background),
    textColor: str(palette.text),
    accents: Array.isArray(palette.accents) ? palette.accents.filter((a) => typeof a === "string").join(", ") : "",
    headingFont: str(fonts.heading),
    bodyFont: str(fonts.body),
    tone: str(kit.tone),
    knowledgeBase: profile?.knowledge_base_collection_uuid ?? "",
  };
}

const orNull = (value: string) => value.trim() || null;

function toBrandKit(v: CompanyFormValues): CompanyProfileUpdate["brand_kit"] {
  const accents = splitAccents(v.accents);
  const palette = {
    primary: orNull(v.primary),
    secondary: orNull(v.secondary),
    accents: accents.length > 0 ? accents : null,
    background: orNull(v.background),
    text: orNull(v.textColor),
  };
  const fonts = { heading: orNull(v.headingFont), body: orNull(v.bodyFont) };
  const hasPalette = Object.values(palette).some((x) => x !== null);
  const hasFonts = Object.values(fonts).some((x) => x !== null);
  const tone = orNull(v.tone);
  if (!(hasPalette || hasFonts || tone)) return null;
  return { palette: hasPalette ? palette : null, fonts: hasFonts ? fonts : null, tone };
}

/** A stable key for a leadership row added in the form. */
export const newRowId = () => crypto.randomUUID();

/** Form values → PUT /admin/company body (empty strings become null, like the old UI). */
export function formToBody(v: CompanyFormValues): CompanyProfileUpdate {
  const leadership = v.leadership
    .map((row) => ({ name: row.name.trim(), title: row.title.trim() }))
    .filter((row) => row.name);
  return {
    company_name: v.companyName.trim(),
    industry: orNull(v.industry),
    website: orNull(v.website),
    description: orNull(v.description),
    mission: orNull(v.mission),
    values: orNull(v.values),
    founded_year: v.foundedYear.trim() ? Number(v.foundedYear) : null,
    headquarters: orNull(v.headquarters),
    employee_count: orNull(v.employeeCount),
    products_services: orNull(v.productsServices),
    leadership: leadership.length > 0 ? leadership : null,
    key_terminology: orNull(v.keyTerminology),
    brand_style: orNull(v.brandStyle),
    brand_kit: toBrandKit(v),
    knowledge_base_collection_uuid: v.knowledgeBase || null,
  };
}
